import { useMemo, useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { PageHero, PdfTile, ClientModal } from '@components/ui'
import { useClientModal } from '@contexts/ClientModalContext'
import { useIsPortrait } from '@hooks/useMediaQuery'
import { useMechanicPendingClients, useMechanicAllClients } from '@hooks/useMechanicData'
import { useWorkOrders } from '@hooks/useWorkOrders'
import { useAuthStore } from '@store/useAuthStore'
import { useQuery } from 'react-query'
import { formatStatus } from '@utils/format'
import { CompanySummary } from '../types/companySummary'
import { companyService, Company } from '@services/companyService'
import { userService } from '@services/userService'

const matchesSearch = (client: CompanySummary, term: string) => {
  if (!term) return true
  const haystack = [client.name, client.address, client.idAdmin]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return haystack.includes(term)
}

type ClientFilter = 'all' | 'ongoing' | 'mine'

export function MechanicClientsPage() {
  const { user } = useAuthStore() 
  const { setModalState } = useClientModal()
  const isPortrait = useIsPortrait()
  const [params] = useSearchParams()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<ClientFilter>('ongoing')
  const [selectedClient, setSelectedClient] = useState<Company | CompanySummary | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoadingClient, setIsLoadingClient] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false) 

  const isMechanic = user?.functionId === 2
  const isAdmin = user?.functionId === 1

  const mechanicId = isMechanic
    ? params.get('mechanicId')
      ? Number(params.get('mechanicId'))
      : user?.id
    : isAdmin
      ? user?.id // For admin, use their ID as the mechanic ID for "mine" filter
      : undefined
  const workshopCompanyId = params.get('workshopId')
    ? Number(params.get('workshopId'))
    : user?.workshopCompanyId

  // Fetch all clients
  const {
    data: allClients = [],
    isLoading: isLoadingAllClients,
    isError: isErrorAllClients,
    refetch: refetchAllClients,
  } = useMechanicAllClients({
    workshopCompanyId,
    mechanicId,
  })

  // Fetch pending clients (ongoing work orders)
  const {
    data: pendingClients = [],
    isLoading: isLoadingPendingClients,
    isError: isErrorPendingClients,
    refetch: refetchPendingClients,
  } = useMechanicPendingClients({
    workshopCompanyId,
    mechanicId,
  })

  const {
    data: workOrdersFromApi = [],
  } = useWorkOrders({
    workshopCompanyId,
    page: 0,
    size: 100,
  })

  // Fetch all users from workshop to get mechanic names
  const { data: workshopUsers = [] } = useQuery(
    ['workshopUsers', workshopCompanyId],
    () => userService.getByWorkshop(workshopCompanyId as number),
    {
      enabled: !!workshopCompanyId,
    }
  )

  // Create a map of user IDs to names
  const userNameMap = useMemo(() => {
    const map = new Map<number, string>()
    workshopUsers.forEach((user) => {
      map.set(user.id, user.name)
    })
    return map
  }, [workshopUsers])

  const latestStatusByClientId = useMemo(() => {
    const map = new Map<number, { status: string; createdAt?: string; assignedMechanicId?: number; assignedMechanicName?: string; createdBy?: number }>()

    workOrdersFromApi.forEach((order) => {
      if (!order.customerCompanyId) {
        return
      }

      const existing = map.get(order.customerCompanyId)
      const createdAt = order.createdAt ? Date.parse(order.createdAt) : 0
      const existingCreatedAt = existing?.createdAt ? Date.parse(existing.createdAt) : 0

      if (!existing || createdAt > existingCreatedAt) {
        map.set(order.customerCompanyId, {
          status: order.status,
          createdAt: order.createdAt,
          assignedMechanicId: order.assignedMechanicId,
          assignedMechanicName: order.assignedMechanicId ? userNameMap.get(order.assignedMechanicId) : undefined,
          createdBy: order.createdBy
        })
      }
    })

    return map
  }, [workOrdersFromApi, userNameMap])

  const getClientWorkOrderStatus = useCallback(
    (clientId: number) => {
      const entry = latestStatusByClientId.get(clientId)
      // Exclude resolved/completed work orders
      const excludedStatuses = ['RESOLVIDA', 'RESOLVED', 'COMPLETED', 'CONCLUÍDA', 'CONCLUIDA', 'CANCELADA', 'CANCELLED']
      if (entry?.status && excludedStatuses.includes(entry.status.toUpperCase())) {
        return null
      }
      return entry?.status ?? null
    },
    [latestStatusByClientId]
  )

  const getClientAssignedMechanic = useCallback(
    (clientId: number) => {
      const entry = latestStatusByClientId.get(clientId)
      return entry?.assignedMechanicName ?? null
    },
    [latestStatusByClientId]
  )

  const searchTerm = search.trim().toLowerCase()

  // Filter clients based on selected filter
  const filteredClients = useMemo(() => {
    let baseClients: CompanySummary[] = []

    switch (filter) {
      case 'all':
        baseClients = allClients
        break
      case 'ongoing':
        baseClients = pendingClients
        break
      case 'mine':
        // For mechanics: filter by assignedMechanicId
        // For admins: filter by createdBy (work orders created by this admin)
        baseClients = allClients.filter((client) => {
          const entry = latestStatusByClientId.get(client.id)
          if (isMechanic) {
            return entry?.assignedMechanicId === mechanicId
          } else if (isAdmin) {
            return entry?.createdBy === user?.id
          }
          return false
        })
        break
      default:
        baseClients = allClients
    }

    return baseClients.filter((client) => matchesSearch(client, searchTerm))
  }, [filter, allClients, pendingClients, searchTerm, latestStatusByClientId, mechanicId, isMechanic, isAdmin, user?.id])

  const isLoading = filter === 'all' ? isLoadingAllClients : isLoadingPendingClients
  const isError = filter === 'all' ? isErrorAllClients : isErrorPendingClients

  const openClientModal = async (client: CompanySummary) => {
    setIsLoadingClient(true)
    setIsModalOpen(true)
    setSelectedClient(client)

    try {
      // Tentar buscar informações completas do cliente (incluindo NIF, telefone, email)
      const fullClient = await companyService.getDetails(client.id)
      setSelectedClient(fullClient)
    } catch (error) {
      console.error('Erro ao carregar informações do cliente:', error)
      // Se getDetails falhar, tentar getById como fallback
      try {
        const fullClient = await companyService.getById(client.id)
        setSelectedClient(fullClient)
      } catch (fallbackError) {
        console.error('Erro ao carregar informações do cliente (fallback):', fallbackError)
        // Se falhar, usar os dados básicos do CompanySummary
        setSelectedClient(client)
      }
    } finally {
      setIsLoadingClient(false)
    }
  }

  const handleDownloadPdf = useCallback(async () => {
    if (!selectedClient) return
    try {
      setIsGeneratingPdf(true)
      await companyService.downloadPdfDirect(selectedClient.id)
    } catch (error: any) {
      console.error('Erro ao fazer download do PDF do cliente:', error)
      alert(`Erro ao fazer download do PDF do cliente: ${error?.message || 'Erro desconhecido'}. Tente novamente.`)
    } finally {
      setIsGeneratingPdf(false)
    }
  }, [selectedClient])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedClient(null)
  }, [])

  const handleRefetch = useCallback(async () => {
    console.log('Refreshing clients...')
    try {
      await refetchAllClients()
      console.log('All clients refetched')
    } catch (error) {
      console.error('Error refetching all clients:', error)
    }
    try {
      await refetchPendingClients()
      console.log('Pending clients refetched')
    } catch (error) {
      console.error('Error refetching pending clients:', error)
    }
  }, [refetchAllClients, refetchPendingClients])

  useEffect(() => {
    setModalState({
      isOpen: isModalOpen,
      client: selectedClient,
      document: null,
      onClose: handleCloseModal,
      onDownloadPdf: handleDownloadPdf,
      isGeneratingPdf: isGeneratingPdf,
      isLoadingClient: isLoadingClient,
    })
    return () => setModalState({ isOpen: false, client: null, document: null, onClose: () => {}, onDownloadPdf: async () => {}, isGeneratingPdf: false, isLoadingClient: false })
  }, [isModalOpen, selectedClient, isGeneratingPdf, isLoadingClient, handleCloseModal, handleDownloadPdf, setModalState])

  return (
    <div className="space-y-8">
      {!isPortrait && isModalOpen && (
        <ClientModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          client={selectedClient}
          document={null}
          onDownloadPdf={handleDownloadPdf}
          isGeneratingPdf={isGeneratingPdf}
          isLoadingClient={isLoadingClient}
        />
      )}
      <PageHero
        title="CLIENTES"
        searchPlaceholder="Pesquisar clientes por nome ou identificação..."
        searchValue={search}
        onSearchChange={setSearch}
      />

      {!workshopCompanyId ? (
        <div className="rounded-3xl border border-yellow-200 bg-yellow-50 p-8 text-yellow-800">
          <div className="flex items-center gap-3 font-semibold">
            <AlertTriangle className="h-5 w-5" />
            É necessário configurar a oficina do mecânico para carregar os clientes.
          </div>
          <p className="mt-2 text-sm">
            Informe o parâmetro <code>workshopId</code> na URL ou associe o mecânico a uma oficina.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition ${
                filter === 'all'
                  ? 'border-secondary-900 bg-secondary-900'
                  : 'border-secondary-200 bg-white text-secondary-900 hover:border-secondary-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <p className={`text-xs font-semibold uppercase tracking-[0.3em] ${filter === 'all' ? 'text-gray-300' : 'text-secondary-400'}`}>
                  Total de clientes no sistema
                </p>
                <p className={`text-sm md:text-lg font-bold ${filter === 'all' ? 'text-white' : 'text-secondary-900'}`}>
                  {isLoadingAllClients ? '...' : allClients.length}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setFilter('ongoing')}
              className={`rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition ${
                filter === 'ongoing'
                  ? 'border-secondary-900 bg-secondary-900'
                  : 'border-secondary-200 bg-white text-secondary-900 hover:border-secondary-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <p className={`text-xs font-semibold uppercase tracking-[0.3em] ${filter === 'ongoing' ? 'text-gray-300' : 'text-secondary-400'}`}>
                  Clientes com folhas pendentes
                </p>
                <p className={`text-sm md:text-lg font-bold ${filter === 'ongoing' ? 'text-white' : 'text-secondary-900'}`}>{pendingClients.length}</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setFilter('mine')}
              className={`rounded-3xl border px-4 py-3 md:px-6 md:py-4 transition ${
                filter === 'mine'
                  ? 'border-secondary-900 bg-secondary-900'
                  : 'border-secondary-200 bg-white text-secondary-900 hover:border-secondary-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <p className={`text-xs font-semibold uppercase tracking-[0.3em] ${filter === 'mine' ? 'text-gray-300' : 'text-secondary-400'}`}>
                  Minhas folhas
                </p>
                <p className={`text-sm md:text-lg font-bold ${filter === 'mine' ? 'text-white' : 'text-secondary-900'}`}>
                  {allClients.filter((client) => {
                    const entry = latestStatusByClientId.get(client.id)
                    if (isMechanic) {
                      return entry?.assignedMechanicId === mechanicId
                    } else if (isAdmin) {
                      return entry?.createdBy === user?.id
                    }
                    return false
                  }).length}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={handleRefetch}
              aria-label="Recarregar lista de clientes"
              className="inline-flex items-center justify-center rounded-full border border-secondary-200 p-2 text-sm font-semibold text-secondary-600 transition hover:border-secondary-300 hover:text-secondary-800"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>

          {isError ? (
            <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-red-700">
              Não foi possível carregar os clientes. Tente novamente.
            </div>
          ) : isLoading ? (
            <div className="grid min-h-[200px] place-items-center rounded-3xl border border-secondary-200 bg-white">
              <p className="text-secondary-500">A carregar clientes associados...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="rounded-3xl border border-secondary-200 bg-white p-10 text-center">
              <p className="text-lg font-semibold text-secondary-700">
                Nenhum cliente associado às folhas pendentes.
              </p>
              <p className="text-secondary-500">
                Quando novas folhas de obra estiverem pendentes, os clientes aparecerão aqui.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 max-h-[min(22rem,58vh)] md:max-h-none overflow-y-auto md:overflow-visible scrollbar-hide">
              {filteredClients.map((client) => {
                const latestStatus = getClientWorkOrderStatus(client.id)
                const assignedMechanic = getClientAssignedMechanic(client.id)
                return (
                  <div key={client.id} className="flex flex-col items-center gap-2 sm:gap-3">
                    <PdfTile
                      label={client.name}
                      details={
                        latestStatus
                          ? [
                              {
                                text: `Última folha: ${formatStatus(latestStatus)}`,
                                className: 'border-blue-200 bg-blue-50 text-blue-700',
                              },
                              {
                                text: assignedMechanic ?? 'Sem mecânico',
                                className: 'border-gray-200 bg-gray-50 text-gray-600',
                              },
                            ]
                          : undefined
                      }
                      onClick={() => openClientModal(client)}
                    />
                    <div className="text-center text-xs text-secondary-500">
                      {client.address ?? 'Endereço não disponível'}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
